import { useEffect, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import Pagination from 'react-js-pagination';
import Select from 'react-select';

import ViewItemsPerPage from "../ViewItemsPerPage";
import { Link } from "react-router-dom";
import { Enumeration, OptionType, ListProperty, SearchProperty, StatusItem } from "../../utils/interface";
import SearchableDropdown from "../SearchableDropdown";
import { formatEnumsForDropDown, formatStatuesForDropDown } from "../../utils/helperFunction";

interface ListPropertiesProps {
  propertiesList: ListProperty[];
  propertiesListCount: number;
  propertiesListPageIndex: number;
  propertiesListPageSize: number;
  onChangePageIndex: (pageNumber: number) => void;
  onChangePageSize: (pageSize: number) => void;
  findOptions: SearchProperty;
  handleFindProperty: (findOptions: SearchProperty) => void;
  resetFindOptions: () => void;
  propertyStatuses: StatusItem[];
  propertyTypes: Enumeration[];
  fetchOwners: (searchTerm: string, pageIndex: number, pageSize: number) =>
    Promise<{ options: { value: number; label: string; }[], hasMore: boolean }>;
}

const ListProperties: React.FC<ListPropertiesProps> = ({
  propertiesList,
  propertiesListCount,
  propertiesListPageIndex,
  propertiesListPageSize,
  onChangePageIndex,
  onChangePageSize,
  findOptions,
  handleFindProperty,
  resetFindOptions,
  fetchOwners,
  propertyStatuses,
  propertyTypes,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [searchOptions, setSearchOptions] = useState(findOptions);
  const [selectedOwner, setSelectedOwner] = useState<OptionType | null>(null);

  const handleModalClose = () => setShowModal(false);
  const handleModalOpen = () => setShowModal(true);

  const onChangeFieldValue = (value: object) => {
    setSearchOptions({ ...searchOptions, ...value });
  }
  const onClickFind = (findOptions: SearchProperty) => {
    handleFindProperty(findOptions);
    handleModalClose();
  };
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeFieldValue({ isVerified: event.target.value === 'yes' });
  };

  useEffect(() => { setSearchOptions(findOptions) }, [findOptions])

  return (<>
    <Button variant="secondary" className="ml-1 mb-3" onClick={handleModalOpen}>
      Find Options
    </Button>
    <Modal show={showModal} onHide={handleModalClose}>
      <Modal.Header closeButton>
        <Modal.Title>Find Property</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Button
          className="mb-3"
          variant="secondary"
          onClick={() => {
            resetFindOptions();
            setSelectedOwner(null);
          }}>
          Clear Parameters
        </Button>
        <Form>
          <Form.Group className="mb-3" controlId="find-property-id">
            <Form.Label>Property ID</Form.Label>
            <Form.Control
              type="text"
              value={searchOptions.propertyId ? searchOptions.propertyId : ''}
              onChange={(e) => onChangeFieldValue({ propertyId: parseInt(e.target.value) })}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="find-property-type">
            <Form.Label><strong>Property Type</strong></Form.Label>
            <Select
              options={formatEnumsForDropDown(propertyTypes)}
              isSearchable
              placeholder=""
              value={
                formatEnumsForDropDown(propertyTypes)
                  .find(option => option.value === findOptions.propertyTypeEnumId)
              }
              onChange={(selectedOption) => {
                if (selectedOption) {
                  onChangeFieldValue({ propertyTypeEnumId: selectedOption?.value })
                }
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="is-verified-property">
            <Form.Label>Is Verified?</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="Yes"
                value="yes"
                checked={searchOptions.isVerified !== undefined ? searchOptions.isVerified === true : false}
                onChange={handleRadioChange}
                name="isVerified"
                id="isVerifiedYes"
              />
              <Form.Check
                inline
                type="radio"
                label="No"
                value="no"
                checked={searchOptions.isVerified !== undefined ? searchOptions.isVerified === false : false}
                onChange={handleRadioChange}
                name="isVerified"
                id="isVerifiedNo"
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-3" controlId="find-property-title">
            <Form.Label>Property Title</Form.Label>
            <Form.Control
              type="text"
              value={searchOptions.propertyTitle ? searchOptions.propertyTitle : ''}
              onChange={(e) => onChangeFieldValue({ propertyTitle: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="find-owner">
            <Form.Label>Owner</Form.Label>
            <SearchableDropdown
              placeholder="Search owner..."
              fetchData={fetchOwners}
              onChange={(selectedValue) => {
                onChangeFieldValue({ ownerId: selectedValue.value });
                setSelectedOwner(selectedValue);
              }}
              selectedData={selectedOwner}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="find-status">
            <Form.Label>Status</Form.Label>
            <Select
              options={formatStatuesForDropDown(propertyStatuses)}
              isSearchable
              placeholder=""
              value={
                formatStatuesForDropDown(propertyStatuses)
                  .find(option => option.value === findOptions.statusId)
              }
              onChange={(selectedOption) => {
                if (selectedOption) {
                  onChangeFieldValue({ statusId: selectedOption.value })
                }
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose}>Close</Button>
        <Button variant="primary" onClick={() => onClickFind(searchOptions)}>Find</Button>
      </Modal.Footer>
    </Modal>
    <Table striped hover size="sm">
      <thead>
        <tr>
          <th>Property#</th>
          <th>Title</th>
          <th>Type</th>
          <th>Verified</th>
          <th>Owner</th>
          <th>Status</th>
          <th>Posted On</th>
        </tr>
      </thead>
      <tbody>
        {propertiesList.map((property: ListProperty) => (
          <tr key={property.propertyId}>
            <td>
              <Link to={`${property.propertyId}`}>
                {property.propertyId}
              </Link>
            </td>
            <td>
              <Link to={`${property.propertyId}`}>
                {property.propertyTitle}
              </Link>
            </td>
            <td>{property.propertyTypeEnumDescription}</td>
            <td>{property.isVerified ? "Yes" : "No"}</td>
            <td>{property.ownerFullName}</td>
            <td>{property.statusDescription}</td>
            <td>{property.createdAt}</td>
          </tr>
        ))}
      </tbody>
    </Table>
    <Pagination
      activePage={propertiesListPageIndex + 1}
      totalItemsCount={propertiesListCount}
      itemsCountPerPage={propertiesListPageSize}
      onChange={onChangePageIndex}
      itemClass="page-item"
      linkClass='page-link'
      firstPageText="First"
      lastPageText="Last"
    />
    <ViewItemsPerPage
      selectedPageSize={propertiesListPageSize}
      onSelectPageSize={onChangePageSize}
    />
  </>);
};

export default ListProperties;
